<template>
  <div class="manual-score-entry p-4">
    <div class="header mb-4">
      <h5 class="mb-2">
        <v-icon name="edit" scale="1.2" class="mr-2" />
        Ingresar Puntajes Manualmente
      </h5>
      <p class="text-muted mb-0">
        Ingrese los puntajes obtenidos por el estudiante en cada pregunta del material.
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Cargando preguntas..."></b-spinner>
      <p class="text-muted mt-2">Cargando preguntas...</p>
    </div>

    <!-- Content -->
    <div v-else>
      <!-- Tabla de preguntas -->
      <b-table
        :items="questions"
        :fields="fields"
        responsive
        hover
        striped
        class="questions-table"
      >
        <!-- Número de pregunta -->
        <template #cell(index)="data">
          <div class="question-number">
            <b-badge variant="primary" pill class="px-3 py-2">
              {{ data.index + 1 }}
            </b-badge>
          </div>
        </template>

        <!-- Texto de la pregunta -->
        <template #cell(question)="data">
          <div class="question-text">
            <div v-html="truncateQuestion(data.item.question)"></div>
          </div>
        </template>

        <!-- Tipo de pregunta -->
        <template #cell(kind)="data">
          <b-badge :variant="getQuestionTypeVariant(data.item.kind)" class="px-2 py-1">
            {{ questionTypeLabel(data.item.kind) }}
          </b-badge>
        </template>

        <!-- Puntaje máximo -->
        <template #cell(max_score)="data">
          <div class="max-score">
            <strong class="text-primary">{{ data.item.max_score }}</strong>
            <small class="text-muted ml-1">pts</small>
          </div>
        </template>

        <!-- Input para puntaje obtenido -->
        <template #cell(score)="data">
          <!-- Si tiene rúbrica válida, mostrar botón -->
          <div v-if="hasValidRubric(data.item)" class="rubric-controls text-center">
            <b-button
              variant="primary"
              pill
              size="sm"
              :class="data.item.chosen_indicators && data.item.chosen_indicators.length ? '' : 'pulsing'"
              @click="openRubricModal(data.item)"
            >
              <v-icon name="th-list" class="mr-1" scale="0.9" />
              Corregir con rúbrica
            </b-button>
            <div class="mt-2">
              <strong class="text-success">{{ data.item.current_score || 0 }}</strong>
              <span class="text-muted"> / {{ data.item.max_score }} pts</span>
            </div>
          </div>
          
          <!-- Sin rúbrica: input normal -->
          <b-form-group v-else class="mb-0">
            <b-input-group>
              <b-form-input
                v-model.number="data.item.current_score"
                type="number"
                :min="0"
                :max="data.item.max_score"
                :state="validateScore(data.item)"
                @input="handleScoreChange(data.item)"
                class="text-center score-input"
                :placeholder="`0-${data.item.max_score}`"
              />
              <b-input-group-append>
                <b-input-group-text>/ {{ data.item.max_score }}</b-input-group-text>
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback :state="validateScore(data.item)">
              Debe ser entre 0 y {{ data.item.max_score }}
            </b-form-invalid-feedback>
          </b-form-group>
        </template>
      </b-table>
      
      <!-- Modal de rúbrica -->
      <rubric-review-modal 
        ref="rubric-review" 
        @set-indicators="handleRubricIndicators"
      />

      <!-- Resumen de puntajes -->
      <div class="score-summary mt-4 p-4 bg-light rounded shadow-sm">
        <b-row align-v="center">
          <b-col cols="12" md="4" class="mb-3 mb-md-0">
            <div class="summary-item">
              <div class="summary-label text-muted">Total Obtenido</div>
              <div class="summary-value">
                <span class="display-4 text-success font-weight-bold">{{ totalScore }}</span>
                <span class="h5 text-muted">/ {{ totalMaxScore }} pts</span>
              </div>
            </div>
          </b-col>
          <b-col cols="12" md="4" class="mb-3 mb-md-0">
            <div class="summary-item">
              <div class="summary-label text-muted">Porcentaje</div>
              <div class="summary-value">
                <span class="display-4 font-weight-bold" :class="getPercentageClass()">
                  {{ percentage }}%
                </span>
              </div>
            </div>
          </b-col>
          <b-col cols="12" md="4">
            <div class="summary-item">
              <div class="summary-label text-muted">Estado</div>
              <div class="summary-value">
                <b-badge :variant="getStatusVariant()" class="px-3 py-2 h5 mb-0">
                  {{ getStatusLabel() }}
                </b-badge>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Botones de acción -->
      <div class="actions mt-4 d-flex justify-content-between align-items-center">
        <div>
          <small class="text-muted">
            <v-icon name="info-circle" scale="0.9" class="mr-1" />
            Los puntajes se guardarán automáticamente y el material se marcará como revisado.
          </small>
        </div>
        <b-button
          variant="success"
          size="lg"
          @click="saveScores"
          class="px-4"
        >
          <b-spinner small v-if="saving" class="mr-2"></b-spinner>
          <v-icon v-else name="save" class="mr-2" />
          {{ saving ? 'Guardando...' : 'Guardar Puntajes' }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import customMaterialService from "@/services/customMaterialService";
import RubricReviewModal from "@/components/custom-materials/modals/RubricReviewModal.vue";

export default {
  name: "ManualScoreEntry",
  components: {
    RubricReviewModal
  },
  props: {
    materialLinkId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      saving: false,
      questions: [],
      originalScores: {},
      fields: [
        { key: 'index', label: '#', thStyle: { width: '80px' }, tdClass: 'text-center' },
        { key: 'question', label: 'Pregunta' },
        { key: 'kind', label: 'Tipo', thStyle: { width: '120px' }, tdClass: 'text-center' },
        { key: 'max_score', label: 'Puntaje Máx.', thStyle: { width: '130px' }, tdClass: 'text-center' },
        { key: 'score', label: 'Puntaje Obtenido', thStyle: { width: '200px' } }
      ]
    };
  },
  computed: {
    totalScore() {
      return this.questions.reduce((sum, q) => sum + (q.current_score || 0), 0);
    },
    totalMaxScore() {
      return this.questions.reduce((sum, q) => sum + q.max_score, 0);
    },
    percentage() {
      return this.totalMaxScore > 0
        ? Math.round((this.totalScore / this.totalMaxScore) * 100)
        : 0;
    },
    isValid() {
      return this.questions.every(q =>
        q.current_score !== null &&
        q.current_score !== undefined &&
        q.current_score >= 0 &&
        q.current_score <= q.max_score
      );
    },
    hasChanges() {
      return this.questions.some(q =>
        q.current_score !== this.originalScores[q.id]
      );
    }
  },
  created() {
    this.loadQuestions();
  },
  methods: {
    async loadQuestions() {
      try {
        console.log("[MANUAL SCORE ENTRY] ===== LOADING QUESTIONS =====");
        console.log("Material Link ID:", this.materialLinkId);
        
        this.loading = true;
        const response = await customMaterialService.getManualScores(this.materialLinkId);
        
        console.log("[MANUAL SCORE ENTRY] Response completo:", response);
        console.log("[MANUAL SCORE ENTRY] response.data:", response.data);
        console.log("[MANUAL SCORE ENTRY] response.questions:", response.questions);
        
        // El servicio ya extrae .data, así que response ES la data
        this.questions = response.questions || [];
        
        console.log("[MANUAL SCORE ENTRY] Questions asignadas:", this.questions);
        console.log("[MANUAL SCORE ENTRY] Total de preguntas:", this.questions.length);
        
        // Guardar puntajes originales para detectar cambios
        this.originalScores = {};
        this.questions.forEach(q => {
          this.originalScores[q.id] = q.current_score;
        });
        
        console.log("[MANUAL SCORE ENTRY] ===== FIN LOADING =====");
      } catch (error) {
        console.error("[MANUAL SCORE ENTRY] Error al cargar preguntas:", error);
        this.$toasted.error('Error al cargar las preguntas');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async saveScores() {
      try {
        this.saving = true;
        const scores = this.questions.map(q => ({
          question_id: q.id,
          score: q.current_score || 0,
          chosen_indicators: q.chosen_indicators || []
        }));

        await customMaterialService.saveManualScores(this.materialLinkId, scores);

        this.$toasted.success('✓ Puntajes guardados exitosamente');
        
        // Actualizar puntajes originales
        this.questions.forEach(q => {
          this.originalScores[q.id] = q.current_score;
        });
        
        this.$emit('scores-saved');
      } catch (error) {
        this.$toasted.error('Error al guardar los puntajes');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    validateScore(question) {
      const score = question.current_score;
      if (score === null || score === undefined) return null;
      return score >= 0 && score <= question.max_score;
    },
    handleScoreChange(question) {
      // Validación en tiempo real
      if (question.current_score > question.max_score) {
        question.current_score = question.max_score;
      }
      if (question.current_score < 0) {
        question.current_score = 0;
      }
      
      // Si el input está vacío, asignar 0
      if (question.current_score === '' || question.current_score === null) {
        question.current_score = 0;
      }
    },
    truncateQuestion(text) {
      if (!text) return '';
      const maxLength = 100;
      if (text.length > maxLength) {
        return text.substring(0, maxLength) + '...';
      }
      return text;
    },
    questionTypeLabel(kind) {
      const types = {
        0: 'Alternativas',
        1: 'Desarrollo',
        2: 'Dictado',
        3: 'Carta',
        4: 'Noticia',
        5: 'Rompecabezas',
        6: 'Cubos',
        7: 'Matriz',
        8: 'Búsqueda',
        9: 'Claves',
        11: 'Escritura',
        12: 'Lectura',
        13: 'Con eje',
        14: 'Fono PK-A',
        15: 'Fono PK-B',
        16: 'Fono PK-C',
        17: 'Fono PK-D',
        18: 'Fono PK-E',
        19: 'Fono PK-F',
        20: 'Fono PK-G',
        21: 'Fono PK-H',
        22: 'Fono K-I',
        23: 'Fono K-J',
      };
      return types[kind] || 'Otro';
    },
    getQuestionTypeVariant(kind) {
      const variants = {
        0: 'info',
        1: 'warning',
        2: 'secondary',
        3: 'primary',
        4: 'primary',
        5: 'success',
        6: 'success',
        7: 'success',
        8: 'danger',
        9: 'dark'
      };
      return variants[kind] || 'secondary';
    },
    getPercentageClass() {
      if (this.percentage >= 70) return 'text-success';
      if (this.percentage >= 30) return 'text-warning';
      return 'text-danger';
    },
    getStatusVariant() {
      if (this.percentage >= 70) return 'success';
      if (this.percentage >= 30) return 'warning';
      return 'danger';
    },
    getStatusLabel() {
      if (this.percentage >= 70) return 'Logrado';
      if (this.percentage >= 30) return 'Parcialmente Logrado';
      return 'No Logrado';
    },
    // Métodos para rúbricas
    hasValidRubric(question) {
      return question.rubric && 
             question.rubric.id && 
             question.rubric.levels && 
             question.rubric.levels.length > 0 && 
             question.rubric.indicators && 
             question.rubric.indicators.length > 0;
    },
    openRubricModal(question) {
      this.$refs['rubric-review'].open(
        question.rubric,
        question.chosen_indicators || [],
        question.id
      );
    },
    handleRubricIndicators(chosenIndicators, questionId, score, maxScore) {
      const question = this.questions.find(q => q.id === questionId);
      if (question) {
        question.chosen_indicators = chosenIndicators;
        question.current_score = score;
        // Actualizar max_score si es necesario
        if (maxScore > 0) {
          question.max_score = maxScore;
        }
      }
    }
  }
};
</script>

<style scoped>
.manual-score-entry {
  background: white;
  border-radius: 8px;
}

.header h5 {
  color: #2c3e50;
  font-weight: 600;
}

.questions-table {
  font-size: 0.95rem;
}

.question-number .badge {
  font-size: 1rem;
  min-width: 45px;
}

.question-text {
  line-height: 1.5;
  color: #495057;
}

.max-score {
  font-size: 1.1rem;
}

.score-input {
  font-weight: 600;
  font-size: 1rem;
}

.score-input:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}

.score-summary {
  border: 2px solid #e0e0e0;
}

.summary-item {
  text-align: center;
}

.summary-label {
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.summary-value {
  font-size: 1.5rem;
}

.actions {
  flex-wrap: wrap;
}

.actions small {
  display: inline-block;
  max-width: 400px;
}

@media (max-width: 768px) {
  .actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .actions > div {
    width: 100%;
    text-align: center;
  }
  
  .actions button {
    width: 100%;
  }
}

/* Animación para botón guardar */
.btn-success:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  transition: all 0.2s;
}

/* Estilos para rúbricas */
.rubric-controls {
  min-width: 180px;
}

/* Animación pulsante para botón de rúbrica sin corregir */
.pulsing {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 123, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
  }
}
</style>

